import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Type, Palette, Layers, Grid, CheckCircle,
  ChevronRight, Moon, Sun, ArrowUpRight,
} from 'lucide-react';
import {
  PageShell, Section, SectionLabel, Heading,
  Card, CardHeader, RowList, Row, Badge, Stat, Divider,
} from '@/features/design-language/designLanguagePrimitives';

// ── Typography specimens ─────────────────────────────────────────────────────

const FONT_SPECIMENS = [
  {
    label: 'fieldwork',
    role: 'Headings / UI structure',
    tailwind: 'font-heading',
    sample: 'The quick brown fox',
    note: 'Use font-heading font-semibold. Loaded from Typekit kit upa8wrl.',
  },
  {
    label: 'degular-variable',
    role: 'Body / UI copy',
    tailwind: 'font-body',
    sample: 'The quick brown fox jumps over the lazy dog',
    note: 'Default body font. Use font-body or font-sans.',
  },
  {
    label: 'Spectral',
    role: 'Editorial / display',
    tailwind: 'font-serif',
    sample: 'Ideas compound — commit to the structure.',
    note: 'Reserved for pull-quotes and editorial moments. Loaded from Google Fonts.',
  },
  {
    label: 'JetBrains Mono',
    role: 'Labels / code / eyebrows',
    tailwind: 'font-mono',
    sample: 'UPPERCASE LABEL · 10px · 0.05em',
    note: 'Always uppercase + tracked for labels. Use text-[10px] tracking-wider.',
  },
];

// ── Color swatches ────────────────────────────────────────────────────────────

const COLOR_GROUPS = [
  {
    label: 'Charcoal (neutrals)',
    swatches: [
      { token: 'charcoal-50', hex: '#f8f9fa', dark: false },
      { token: 'charcoal-100', hex: '#f1f3f5', dark: false },
      { token: 'charcoal-200', hex: '#e9ecef', dark: false },
      { token: 'charcoal-400', hex: '#868e96', dark: false },
      { token: 'charcoal-500', hex: '#495057', dark: true },
      { token: 'charcoal-700', hex: '#343a40', dark: true },
      { token: 'charcoal-900', hex: '#212529', dark: true },
    ],
  },
  {
    label: 'Primary (sage green)',
    swatches: [
      { token: 'primary-50', hex: '#f0f7f1', dark: false },
      { token: 'primary-100', hex: '#d8eedb', dark: false },
      { token: 'primary-400', hex: '#74ad78', dark: false },
      { token: 'primary-600', hex: '#5A7D5E', dark: true },
      { token: 'primary-700', hex: '#4a6b4e', dark: true },
    ],
  },
  {
    label: 'Dark mode surfaces',
    swatches: [
      { token: '--bg (#0B0F19)', hex: '#0B0F19', dark: true },
      { token: '--bg-raised (#0D1220)', hex: '#0D1220', dark: true },
      { token: '--surface (#111827)', hex: '#111827', dark: true },
      { token: '--rule (#1A2234)', hex: '#1A2234', dark: true },
    ],
  },
];

// ── Component demos ───────────────────────────────────────────────────────────

const BADGE_VARIANTS = ['default', 'outline', 'green', 'amber', 'red', 'blue'] as const;

// ── Linked sub-pages ──────────────────────────────────────────────────────────

const SUB_PAGES = [
  { path: '/elevation/priorities-ai-design-language/typography', label: 'Typography deep-dive', badge: 'ready' as const },
  { path: '/elevation/priorities-ai-design-language/colors', label: 'Color palette', badge: 'ready' as const },
  { path: '/elevation/priorities-ai-design-language/component-library', label: 'Component Library', badge: 'wip' as const },
  { path: '/elevation/priorities-ai-design-language/brand-elements', label: 'Brand Elements', badge: 'wip' as const },
  { path: '/elevation/priorities-ai-design-language/icon-library', label: 'Icon Library', badge: 'wip' as const },
  { path: '/elevation/design-language', label: 'Legacy hub (Elevation)', badge: 'default' as const },
];

// ─────────────────────────────────────────────────────────────────────────────

export const BrandDesignSystem: React.FC = () => {
  const [darkDemo, setDarkDemo] = useState(false);

  return (
    <PageShell>

      {/* Hero */}
      <Section>
        <Card className="text-center py-10 px-6">
          <SectionLabel>Priorities.ai</SectionLabel>
          <Heading as="h1" size="xl" className="mt-1 mb-3">Brand & Design System</Heading>
          <p className="text-sm text-charcoal-500 dark:text-charcoal-400 max-w-xl mx-auto leading-relaxed">
            One design system. Two modes. Four fonts. Every Elevation page builds from these tokens
            and the shared primitives in <code className="font-mono text-xs bg-charcoal-50 dark:bg-charcoal-800 px-1 rounded">src/components/elevation</code>.
          </p>
          <div className="flex items-center justify-center gap-6 mt-6">
            <Stat label="Fonts" value="4" />
            <Stat label="Color tokens" value="50+" />
            <Stat label="Primitives" value="12" />
            <Stat label="Modes" value="2" />
          </div>
        </Card>
      </Section>

      {/* Typography */}
      <Section>
        <div className="flex items-center gap-2 mb-4">
          <Type className="h-4 w-4 text-charcoal-400" />
          <SectionLabel className="mb-0">Typography</SectionLabel>
        </div>
        <div className="space-y-4">
          {FONT_SPECIMENS.map((spec) => (
            <Card key={spec.label}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <Badge variant="outline" className="mb-2">{spec.tailwind}</Badge>
                  <Heading size="sm">{spec.label}</Heading>
                  <p className="text-xs text-charcoal-500 dark:text-charcoal-400 mt-0.5">{spec.role}</p>
                </div>
              </div>
              <p
                className={`text-2xl text-charcoal-900 dark:text-charcoal-50 mb-3 ${spec.tailwind}`}
                style={{ fontFamily: spec.label === 'fieldwork' ? 'fieldwork' : spec.label === 'degular-variable' ? 'degular-variable' : undefined }}
              >
                {spec.sample}
              </p>
              <p className="text-[11px] font-mono text-charcoal-400 dark:text-charcoal-500">{spec.note}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Divider />

      {/* Colors */}
      <Section>
        <div className="flex items-center gap-2 mb-4">
          <Palette className="h-4 w-4 text-charcoal-400" />
          <SectionLabel className="mb-0">Color Tokens</SectionLabel>
        </div>
        <div className="space-y-6">
          {COLOR_GROUPS.map((group) => (
            <div key={group.label}>
              <Heading size="sm" className="mb-3">{group.label}</Heading>
              <div className="flex flex-wrap gap-2">
                {group.swatches.map((s) => (
                  <div key={s.token} className="flex flex-col items-center gap-1 w-24">
                    <div
                      className="w-full h-10 border border-charcoal-200 dark:border-charcoal-700"
                      style={{ backgroundColor: s.hex }}
                    />
                    <span className="font-mono text-[9px] text-charcoal-500 dark:text-charcoal-400 text-center leading-tight">
                      {s.token}
                    </span>
                    <span className="font-mono text-[9px] text-charcoal-400 dark:text-charcoal-500">{s.hex}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Divider />

      {/* Components */}
      <Section>
        <div className="flex items-center gap-2 mb-4">
          <Layers className="h-4 w-4 text-charcoal-400" />
          <SectionLabel className="mb-0">Component Primitives</SectionLabel>
        </div>

        {/* Badges */}
        <Card className="mb-4">
          <SectionLabel>Badge variants</SectionLabel>
          <div className="flex flex-wrap gap-2 mt-2">
            {BADGE_VARIANTS.map((v) => (
              <Badge key={v} variant={v}>{v}</Badge>
            ))}
          </div>
        </Card>

        {/* Card + Row pattern */}
        <Card flush className="mb-4">
          <CardHeader label="PATTERN" title="Card + RowList" subtitle="flush Card with divide-y rows" />
          <RowList>
            {['Prospector Engine', 'Knowledge Engine', 'Canon Engine'].map((name, i) => (
              <Row key={name}>
                <span className="flex-1 text-sm text-charcoal-700 dark:text-charcoal-200">{name}</span>
                <Badge variant={(['green', 'blue', 'amber'] as const)[i]}>
                  {(['ACTIVE', 'ACTIVE', 'WIP'] as const)[i]}
                </Badge>
                <ChevronRight className="h-4 w-4 text-charcoal-400" />
              </Row>
            ))}
          </RowList>
        </Card>

        {/* Stats */}
        <Card>
          <SectionLabel className="mb-3">Stat grid</SectionLabel>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Stat label="Sessions" value="142" />
            <Stat label="Participants" value="89" />
            <Stat label="Decisions" value="24" />
            <Stat label="Outcomes" value="18" />
          </div>
        </Card>
      </Section>

      <Divider />

      {/* Dark mode demo */}
      <Section>
        <div className="flex items-center gap-2 mb-4">
          {darkDemo ? <Moon className="h-4 w-4 text-charcoal-400" /> : <Sun className="h-4 w-4 text-charcoal-400" />}
          <SectionLabel className="mb-0">Dark Mode</SectionLabel>
          <button
            onClick={() => setDarkDemo(!darkDemo)}
            className="ml-auto text-xs font-mono text-primary-600 dark:text-primary-400 hover:underline"
          >
            {darkDemo ? 'switch to light' : 'switch to dark'}
          </button>
        </div>
        <div className={darkDemo ? 'dark' : ''}>
          <Card className="dark:bg-[var(--surface)]">
            <SectionLabel>Dark surface preview</SectionLabel>
            <Heading size="md" className="mb-1 dark:text-charcoal-50">
              {darkDemo ? 'Dark mode active' : 'Light mode active'}
            </Heading>
            <p className="text-sm text-charcoal-500 dark:text-charcoal-400">
              Cards use <code className="font-mono text-[11px] bg-charcoal-50 dark:bg-charcoal-800 px-1 rounded">bg-[var(--bg-raised)] dark:bg-[var(--surface)]</code>.
              Text uses <code className="font-mono text-[11px] bg-charcoal-50 dark:bg-charcoal-800 px-1 rounded">text-charcoal-900 dark:text-charcoal-50</code>.
            </p>
            <div className="flex gap-2 mt-3">
              <Badge variant="green">token</Badge>
              <Badge variant="outline">outline</Badge>
              <Badge>default</Badge>
            </div>
          </Card>
        </div>
        <p className="text-xs text-charcoal-400 dark:text-charcoal-500 mt-3 font-mono">
          Dark mode works via the <code>.dark &#123; &#125;</code> CSS variable block in src/index.css — any .dark ancestor re-asserts dark tokens.
        </p>
      </Section>

      <Divider />

      {/* Anti-patterns */}
      <Section>
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle className="h-4 w-4 text-charcoal-400" />
          <SectionLabel className="mb-0">Never Use These</SectionLabel>
        </div>
        <Card flush>
          <RowList>
            {[
              ['border-2', 'Use border (single weight)'],
              ['shadow-lg / shadow-md', 'Cards use border only, no shadow'],
              ['bg-gradient-to-*', 'Flat bg-[var(--bg-raised)] dark:bg-[var(--surface)]'],
              ['font-bold', 'Use font-heading font-semibold'],
              ['gray-* tokens', 'Use charcoal-* tokens'],
              ['bg-[var(--mustard-dim)], bg-[var(--slate-dim)]…', 'Colored section backgrounds break dark mode'],
              ['consulting-slate-blue-*', 'Legacy — use primary-* or charcoal-*'],
            ].map(([bad, good]) => (
              <Row key={bad}>
                <code className="font-mono text-[11px] text-[var(--coral)] dark:text-[var(--coral)] w-48 shrink-0">{bad}</code>
                <span className="text-xs text-charcoal-500 dark:text-charcoal-400">{good}</span>
              </Row>
            ))}
          </RowList>
        </Card>
      </Section>

      <Divider />

      {/* Sub-pages */}
      <Section>
        <div className="flex items-center gap-2 mb-4">
          <Grid className="h-4 w-4 text-charcoal-400" />
          <SectionLabel className="mb-0">Design System Sub-Pages</SectionLabel>
        </div>
        <Card flush>
          <RowList>
            {SUB_PAGES.map((p) => (
              <Row key={p.path} className="group">
                <Link to={p.path} className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="flex-1 text-sm text-charcoal-700 dark:text-charcoal-200 group-hover:text-primary-600 transition-colors">
                    {p.label}
                  </span>
                  <Badge variant={p.badge === 'ready' ? 'green' : p.badge === 'wip' ? 'amber' : 'default'}>
                    {p.badge}
                  </Badge>
                  <ArrowUpRight className="h-3.5 w-3.5 text-charcoal-400 group-hover:text-primary-600 transition-colors" />
                </Link>
              </Row>
            ))}
          </RowList>
        </Card>
      </Section>

    </PageShell>
  );
};
